import { mapErrorToUserMessage } from '@repo/core'
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { bankAccountRepo, bankTransactionRepo } from '../../lib/db'
import { AccountingService } from '@repo/entity-accounting'
import { ChevronLeft, Plus, Save, Landmark, CheckCircle, RefreshCw, Calculator } from 'lucide-react'

export function BankAccountDetailPage() {
  const params = useParams({ from: '/bank-accounts/$id' })
  const id = (params as any).id ?? ''
  const navigate = useNavigate()
  const [account, setAccount] = useState<any>(null)
  const [transactions, setTransactions] = useState<any[]>([])
  const [showModal, setShowModal] = useState(false)
  const [showReconModal, setShowReconModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ transactionDate: Date.now(), type: 'deposit', amount: 0, particulars: '', remarks: '' })

  // Bank Reconciliation State
  const [statementBalance, setStatementBalance] = useState<number>(0)
  const [clearedTxIds, setClearedTxIds] = useState<Set<string>>(new Set())
  const [reconSuccess, setReconSuccess] = useState(false)

  const loadData = () => {
    if (!id) return
    Promise.all([
      bankAccountRepo.findById(id),
      bankTransactionRepo.findMany({
        page: 1,
        pageSize: 100,
        filter: [{ field: 'bankAccountId', operator: 'eq', value: id }],
        sort: [{ field: 'transactionDate', direction: 'desc' }],
      }),
    ]).then(([a, t]) => {
      setAccount(a)
      if ('items' in t) {
        setTransactions(t.items)
        const initialCleared = new Set<string>()
        t.items.forEach((item: any) => {
          if (item.isCleared) initialCleared.add(item.id)
        })
        setClearedTxIds(initialCleared)
      }
    })
  }

  useEffect(() => {
    loadData()
  }, [id])

  async function handleTransaction(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await bankTransactionRepo.create({
        ...form,
        bankAccountId: id,
        tenantId: 'default',
        isCleared: false,
        createdBy: 'admin',
        updatedBy: 'admin',
      })
      setShowModal(false)
      setForm({ transactionDate: Date.now(), type: 'deposit', amount: 0, particulars: '', remarks: '' })
      loadData()
    } catch (error) {
      console.error(error)
      alert(mapErrorToUserMessage(error).userMessage)
    } finally {
      setSaving(false)
    }
  }

  const toggleCleared = (txId: string) => {
    const updated = new Set(clearedTxIds)
    if (updated.has(txId)) {
      updated.delete(txId)
    } else {
      updated.add(txId)
    }
    setClearedTxIds(updated)
  }

  if (!account) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate({ to: '/bank-accounts' })} icon={<ChevronLeft className="h-4 w-4" />}>
          Back
        </Button>
        <p className="mt-4 text-gray-400">Loading...</p>
      </div>
    )
  }

  const bookBalance = transactions.reduce((s: number, t: any) => {
    return t.type === 'deposit' ? s + t.amount : s - t.amount
  }, account.startingBalance ?? 0)

  // Reconciliation Calculations
  const clearedDeposits = transactions
    .filter(t => clearedTxIds.has(t.id) && t.type === 'deposit')
    .reduce((s, t) => s + t.amount, 0)
  const clearedWithdrawals = transactions
    .filter(t => clearedTxIds.has(t.id) && t.type === 'withdrawal')
    .reduce((s, t) => s + t.amount, 0)
  const clearedBalance = (account.startingBalance ?? 0) + clearedDeposits - clearedWithdrawals
  const reconciliationDifference = Math.round((statementBalance - clearedBalance) * 100) / 100
  const isReconciled = Math.abs(reconciliationDifference) === 0 && statementBalance > 0

  return (
    <div className="p-6 space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate({ to: '/bank-accounts' })}
        icon={<ChevronLeft className="h-4 w-4" />}
      >
        Back to Accounts
      </Button>

      {reconSuccess && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-emerald-600" />
          <span>Bank Reconciliation completed! Difference is ₱0.00.</span>
        </div>
      )}

      {/* Account Info Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
            <Landmark className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{account.bankName}</h1>
            <p className="text-sm text-gray-500 font-mono">
              {account.accountName} · {account.accountNumber}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Book Balance</p>
            <p className="text-2xl font-bold text-emerald-600 font-mono">
              ₱{bookBalance.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setStatementBalance(bookBalance)
                setShowReconModal(true)
              }}
              icon={<Calculator className="h-4 w-4" />}
            >
              Reconcile Bank
            </Button>
            <Button onClick={() => setShowModal(true)} icon={<Plus className="h-4 w-4" />}>
              New Transaction
            </Button>
          </div>
        </div>
      </div>

      {/* Transactions Register */}
      <Card>
        <CardHeader
          title="Account Register"
          description={`${transactions.length} recorded transactions · Click checkbox to toggle cleared status`}
        />
        {transactions.length === 0 ? (
          <p className="py-12 text-center text-gray-400">No transactions recorded yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase w-12">Cleared</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Particulars / Payee</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Payment / Outflow (₱)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Deposit / Inflow (₱)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {transactions.map((t: any) => {
                  const isCleared = clearedTxIds.has(t.id)
                  return (
                    <tr key={t.id} className={`hover:bg-gray-50 ${isCleared ? 'bg-emerald-50/20' : ''}`}>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={isCleared}
                          onChange={() => toggleCleared(t.id)}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600 font-mono text-xs">
                        {new Date(t.transactionDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-gray-900 font-medium">{t.particulars}</td>
                      <td className="px-4 py-3">
                        <Badge color={t.type === 'deposit' ? 'green' : 'red'} size="sm">
                          {t.type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-red-600 font-medium">
                        {t.type === 'withdrawal' ? `₱${t.amount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-600 font-medium">
                        {t.type === 'deposit' ? `₱${t.amount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}` : '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* New Transaction Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="New Bank Transaction"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleTransaction} loading={saving} icon={<Save className="h-4 w-4" />}>
              Save
            </Button>
          </>
        }
      >
        <form className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={form.type}
              onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-blue-500"
            >
              <option value="deposit">Deposit / Inflow</option>
              <option value="withdrawal">Withdrawal / Payment</option>
            </select>
          </div>
          <Input
            label="Amount (₱)"
            type="number"
            required
            value={String(form.amount)}
            onChange={e => setForm(f => ({ ...f, amount: Number(e.target.value) }))}
          />
          <Input
            label="Particulars / Payee"
            required
            value={form.particulars}
            onChange={e => setForm(f => ({ ...f, particulars: e.target.value }))}
          />
          <Input
            label="Remarks (Optional)"
            value={form.remarks}
            onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
          />
        </form>
      </Modal>

      {/* Bank Reconciliation Modal */}
      {showReconModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Bank Reconciliation</h2>
                <p className="text-xs text-gray-500">{account.bankName} · {account.accountNumber}</p>
              </div>
              <button onClick={() => setShowReconModal(false)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Bank Statement Ending Balance (₱)
                </label>
                <input
                  type="number"
                  value={statementBalance}
                  onChange={e => setStatementBalance(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="bg-gray-50 p-4 rounded-xl space-y-2 border border-gray-200">
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Cleared Deposits ({transactions.filter(t => clearedTxIds.has(t.id) && t.type === 'deposit').length}):</span>
                  <span className="font-mono text-emerald-600 font-medium">₱{clearedDeposits.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Cleared Payments ({transactions.filter(t => clearedTxIds.has(t.id) && t.type === 'withdrawal').length}):</span>
                  <span className="font-mono text-red-600 font-medium">₱{clearedWithdrawals.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-xs font-semibold text-gray-800 border-t pt-1">
                  <span>Cleared Balance:</span>
                  <span className="font-mono">₱{clearedBalance.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-xs font-semibold text-gray-800">
                  <span>General Ledger Book Balance:</span>
                  <span className="font-mono">₱{bookBalance.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                </div>

                <div className={`mt-3 p-2.5 rounded-lg flex justify-between items-center text-sm font-bold ${isReconciled ? 'bg-emerald-100 text-emerald-900' : 'bg-red-100 text-red-900'}`}>
                  <span>Reconciliation Difference:</span>
                  <span className="font-mono">₱{reconciliationDifference.toFixed(2)}</span>
                </div>
              </div>

              {isReconciled ? (
                <div className="p-3 bg-emerald-50 text-emerald-800 rounded-lg text-xs flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                  <span>Statements match perfectly! Ready to finalize reconciliation.</span>
                </div>
              ) : (
                <p className="text-xs text-gray-500">
                  Check or uncheck transactions in the register table until the difference reaches ₱0.00.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
              <Button variant="outline" onClick={() => setShowReconModal(false)}>
                Close
              </Button>
              <Button
                disabled={!isReconciled}
                onClick={() => {
                  setReconSuccess(true)
                  setShowReconModal(false)
                  setTimeout(() => setReconSuccess(false), 5000)
                }}
              >
                Finalize Reconciliation
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
